import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Menu, MenuItem } from "@mui/material";
import { MdKeyboardArrowDown } from "react-icons/md";
import zikoraLogo from "../images/ZikoraLogoWhite 1.svg";
import MobileNav from "./mobileNav";

const NAV_ITEMS = [
	{ label: "Accounts", key: "accounts" },
	{ label: "Loans", key: "loans" },
	{ label: "SME", key: "sme" },
	{ label: "Corporate", key: "corporate" },
	{ label: "E-Banking", key: "ebanking" },
	{ label: "About", key: "about" },
];

const MENU_CONFIG = {
	accounts: [
		{ label: "Savings", to: "/saving-account" },
		{ label: "Current", to: "/current-account" },
		{ label: "Fixed Deposits", to: "/fixed-deposit" },
	],
	loans: [
		{ label: "Loans Overview", to: "/loans" },
		{ label: "School Fees Loan", to: "/school-fees-loan" },
	],
	sme: [
		{ label: "Business Banking", to: "/business-banking" },
	],
	corporate: [
		{ label: "Corporate Login", href: "https://zikoramfb-corporate.lucidbanking.com" },
	],
	ebanking: [
		{ label: "Online Banking", href: "https://zikoramfb.qoreonline.com/login" },
		{ label: "Mobile Banking", to: "/mobile-banking" },
		{ label: "Agency Banking", to: "/agency-banking" },
		{ label: "USSD Banking", to: "/ussd-banking" },
	],
	about: [
		{ label: "About Us", to: "/about" },
		{ label: "Contact Us", to: "/contact" },
		{ label: "Support", to: "/support" },
		{ label: "Blog", to: "/blog" },
	],
};

const dropdownMenuSx = {
	"& .MuiPaper-root": {
		borderRadius: "8px",
		minWidth: "180px",
		boxShadow: "0px 8px 24px rgba(0,0,0,0.25)",
		bgcolor: "#1B251A",
		mt: "4px",
	},
	"& .MuiMenuItem-root": {
		fontFamily: "Raleway, sans-serif",
		fontSize: "14px",
		fontWeight: 500,
		color: "#DEDEDE",
		py: 1.5,
		px: 2.5,
		"&:hover": {
			bgcolor: "rgba(102, 166, 129, 0.15)",
		},
	},
};

const Header = ({ solid = false, compactMobile = false }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [openMenu, setOpenMenu] = useState(null);

	const handleOpen = (key, e) => {
		setAnchorEl(e.currentTarget);
		setOpenMenu(key);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setOpenMenu(null);
	};

	return (
		<>
		<style>{`
			@media (max-width: 600px) {
				.header-compact-mobile, .header-compact-mobile-spacer { height: 66px !important; }
				.header-compact-mobile .header-inner { padding: 0 24px !important; }
				.header-compact-mobile .header-inner img { width: 32px !important; height: 32px !important; }
			}
			@media (max-width: 1199px) {
				.header-desktop-nav { display: none !important; }
				.header-desktop-cta { display: none !important; }
				.header-mobile-nav { display: block !important; }
				.header-inner { padding: 0 24px !important; }
			}
			@media (min-width: 1200px) and (max-width: 1375px) {
				.header-inner { padding: 0 32px !important; }
				.header-desktop-nav { margin-left: 24px !important; gap: 20px !important; }
			}
			.header-mobile-nav { display: none; }
		`}</style>

		{/* Spacer so page content is never hidden behind the fixed header */}
		<div className={compactMobile ? "header-compact-mobile-spacer" : undefined} style={{ height: "88px", flexShrink: 0 }} />
		<header
			className={compactMobile ? "header-compact-mobile" : undefined}
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100vw",
				height: "88px",
				backgroundColor: solid ? "#132A22" : "rgba(19, 42, 34, 0.75)",
				backdropFilter: solid ? "none" : "blur(20px) saturate(1.4)",
				WebkitBackdropFilter: solid ? "none" : "blur(20px) saturate(1.4)",
				boxShadow: solid ? "0px 24px 48px -12px rgba(10, 13, 18, 0.18)" : "0px 24px 48px -12px rgba(10, 13, 18, 0.45)",
				zIndex: 1000,
				display: "flex",
				alignItems: "center",
			}}
		>
			{/* Inner row — full width, responsive horizontal padding */}
			<div
				className="header-inner"
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					padding: "0 80px",
					height: "56px",
				}}
			>
				{/* Logo */}
				<RouterLink to="/" style={{ lineHeight: 0, flexShrink: 0 }}>
					<img
						src={zikoraLogo}
						alt="Zikora MFB"
						style={{ width: "56px", height: "56px" }}
					/>
				</RouterLink>

				{/* Nav items container */}
				<nav
					className="header-desktop-nav"
					style={{
						display: "flex",
						alignItems: "center",
						height: "56px",
						gap: "30px",
						borderRadius: "8px",
						padding: "0 16px",
						marginLeft: "40px",
					}}
				>
					{NAV_ITEMS.map(({ label, key }) => (
						<button
							key={key}
							onClick={(e) => handleOpen(key, e)}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								background: "none",
								border: "none",
								cursor: "pointer",
								fontFamily: solid ? "Geist, sans-serif" : "Raleway, sans-serif",
								fontWeight: 500,
								fontSize: "16px",
								lineHeight: "24px",
								color: "#DEDEDE",
								padding: 0,
								whiteSpace: "nowrap",
							}}
						>
							{label}
							<MdKeyboardArrowDown
								style={{ width: "20px", height: "20px", flexShrink: 0 }}
							/>
						</button>
					))}
				</nav>

				{/* Dropdown menus */}
				{NAV_ITEMS.map(({ key }) => (
					<Menu
						key={key}
						anchorEl={anchorEl}
						open={openMenu === key}
						onClose={handleClose}
						MenuListProps={{ disablePadding: true }}
						sx={dropdownMenuSx}
					>
						{(MENU_CONFIG[key] || []).map((item) =>
							item.href ? (
								<MenuItem
									key={item.label}
									onClick={handleClose}
									component="a"
									href={item.href}
									target="_blank"
									rel="noopener noreferrer"
								>
									{item.label}
								</MenuItem>
							) : (
								<MenuItem
									key={item.label}
									onClick={handleClose}
									component={RouterLink}
									to={item.to}
								>
									{item.label}
								</MenuItem>
							)
						)}
					</Menu>
				))}

				{/* CTA buttons — pushed to right, hidden on mobile */}
				<div
					className="header-desktop-cta"
					style={{
						marginLeft: "auto",
						display: "flex",
						alignItems: "center",
						gap: "12px",
						flexShrink: 0,
					}}
				>
					<a
						href="https://play.google.com/store/apps/details?id=com.clive.zikora"
						target="_blank"
						rel="noopener noreferrer"
						style={{
							height: "44px",
							borderRadius: "8px",
							padding: "0 16px",
							border: "1px solid #DEDEDE",
							fontFamily: solid ? "Geist, sans-serif" : "Raleway, sans-serif",
							fontWeight: 600,
							fontSize: "16px",
							lineHeight: "20px",
							color: "#FFFFFF",
							background: "transparent",
							textDecoration: "none",
							whiteSpace: "nowrap",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						Download the App
					</a>

					<a
						href="https://zikoramfb.qoreonline.com/onboarding/type"
						target="_blank"
						rel="noopener noreferrer"
						style={{
							height: "44px",
							borderRadius: "8px",
							padding: "0 16px",
							background: "#86C7A8",
							fontFamily: solid ? "Geist, sans-serif" : "Raleway, sans-serif",
							fontWeight: 600,
							fontSize: "16px",
							lineHeight: "20px",
							color: "#132A22",
							textDecoration: "none",
							whiteSpace: "nowrap",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						Open Account
					</a>
				</div>

				{/* Mobile nav — visible only on mobile */}
				<div
					className="header-mobile-nav"
					style={{ marginLeft: "auto" }}
				>
					<MobileNav />
				</div>
			</div>
		</header>
		</>
	);
};

export default Header;
